import "./community-hub-interest.scss";

import { useEffect, useState } from "react";
import { TextAreaControl, WVUSForm } from "wvus-forms";

import { triggerCommunityRegistrationView } from "../../helpers/observability/analytics";
import { sendInterestEmail } from "../../services/requests";
import appSettings from "../../settings/appSettings";
import ErrorInfo from "../../views/ErrorInfo";
import Alert from "../alert/alert";
import PrimaryButton from "../buttons/PrimaryButton";
import { useFormFlowContext } from "../forms/FormFlowContext";
import LoadingBeacon from "../loading/LoadingBeacon";

/**
 * CommunityHubInterest component handles the submission of interest forms for joining the Community Hub.
 * It uses WVUSForm for form handling and manages the form submission state.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.formMethods - Methods for handling form state and validation.
 *
 */
const CommunityHubInterest = WVUSForm(({ formMethods }) => {
  const { userData } = useFormFlowContext();
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alertToRetry, setAlertToRetry] = useState(false);

  const { widgetId, wvusLogger } = appSettings;
  const { getFormState } = formMethods;
  const formFields = getFormState().fields;

  useEffect(() => {
    const triggerOnce = true;
    triggerCommunityRegistrationView(
      {
        event_action: "thank_you_for_your_interest",
        event_label: "thank_you_for_your_interest"
      },
      triggerOnce
    );
  }, []);

  if (!formFields) {
    return null;
  }

  const onSubmissionError = (err) => {
    const errorMessage = err?.message || "Error submitting interest form";
    const statusCode = err?.resp?.status || "error";
    wvusLogger.log({ errorMessage, statusCode }, wvusLogger.getLogLevelNames().ERROR);
    setAlertToRetry(true);

    if (statusCode === 400) {
      // reset the form so they can try again
      formMethods.setValueChange("message", "");
    } else {
      setSubmissionError(err);
    }
    setSubmitting(false);
  };

  const handleSubmit = (event) => {
    setSubmitting(true);
    setAlertToRetry(false);

    event.preventDefault();
    const { message } = formFields;
    const data = {
      ...userData,
      registrationType: widgetId,
      message: message?.value
    };

    sendInterestEmail(data)
      .then(() => {
        setSubmissionComplete(true);
      })
      .catch(onSubmissionError)
      .finally(() => setSubmitting(false));
  };

  const FormSubmissionContent = () => {
    switch (true) {
      case submissionComplete:
        return <p className="base">{"We've shared these details with the community manager."}</p>;
      case submitting:
        return <LoadingBeacon />;
      default:
        return null;
    }
  };

  if (submissionError) {
    return (
      <ErrorInfo errorKey="communityHubInterest" errorBack={() => setSubmissionError(false)} />
    );
  }

  return (
    <div className="community-hub-interest-page">
      <div className="community-hub-interest-page-border" />
      <div className="form-container">
        {alertToRetry && (
          <Alert type="warning">Submission was not successful. Please try again.</Alert>
        )}
        <h1 className="serif">
          <strong>Thank you for your interest.</strong>
        </h1>
        {submissionComplete || submitting ? (
          <FormSubmissionContent />
        ) : (
          <>
            <p className="base">We’ve notified the community manager about your interest.</p>
            <form onSubmit={handleSubmit}>
              <TextAreaControl
                fieldName="message"
                fieldTitle="Please briefly share why you would like to join the Community Hub:"
                formMethods={formMethods}
                showUISuccess={false}
              />
              <PrimaryButton type="submit" disabled={!formFields["message"]?.value}>
                Submit
              </PrimaryButton>
            </form>
          </>
        )}
      </div>
    </div>
  );
});

export default CommunityHubInterest;
